<template>
  <el-dialog
    :model-value="visible"
    :title="title"
    width="760px"
    class="org-contact-picker-dialog member-org-picker-dialog"
    destroy-on-close
    append-to-body
    align-center
    @update:model-value="(v: boolean) => emit('update:visible', v)"
    @opened="onOpened"
  >
    <div class="picker-body">
      <section class="picker-left">
        <el-input
          v-model="keyword"
          clearable
          placeholder="搜索姓名、工号、组织"
          class="picker-search"
          @input="onSearchInput"
        >
          <template #prefix>
            <el-icon><Search /></el-icon>
          </template>
        </el-input>

        <template v-if="!isSearching">
          <div class="picker-nav">
            <div class="picker-tabs">
              <button
                type="button"
                :class="{ active: activeTab === 'formal' }"
                @click="switchTab('formal')"
              >组织架构</button>
              <button
                type="button"
                :class="{ active: activeTab === 'virtual' }"
                @click="switchTab('virtual')"
              >虚拟组织</button>
              <button
                type="button"
                :class="{ active: activeTab === 'temp' }"
                @click="switchTab('temp')"
              >临时成员</button>
            </div>

            <nav v-if="activeTab !== 'temp'" class="picker-breadcrumb" aria-label="当前位置">
              <button
                v-for="(crumb, idx) in breadcrumbs"
                :key="crumb.key"
                type="button"
                class="crumb"
                :class="{ current: idx === breadcrumbs.length - 1 }"
                :disabled="idx === breadcrumbs.length - 1"
                @click="goBreadcrumb(idx)"
              >{{ crumb.label }}</button>
            </nav>
            <p v-if="activeTab === 'virtual'" class="picker-hint">
              按当前虚拟组织成员快照加入；之后删除该虚拟组织不会自动移出项目成员。
            </p>
            <p v-else-if="activeTab === 'temp'" class="picker-hint">
              临时成员仅站内通知，无飞书/企微。
            </p>
          </div>

          <div v-loading="loading" class="picker-list">
            <div
              v-for="row in currentRows"
              :key="row.key"
              class="picker-row"
              :class="{ checked: isRowChecked(row), disabled: isRowDisabled(row) }"
            >
              <el-checkbox
                v-if="mode === 'multiple' || row.kind === 'user'"
                :model-value="isRowChecked(row)"
                :disabled="isRowDisabled(row)"
                @change="(v: boolean | string | number) => toggleRow(row, v === true || v === 'true')"
              />
              <span class="picker-row-icon" :class="row.kind">
                <el-icon v-if="row.kind === 'dept'"><OfficeBuilding /></el-icon>
                <el-icon v-else><User /></el-icon>
              </span>
              <span
                class="picker-row-main"
                @click="onRowMainClick(row)"
              >
                <strong>
                  {{ row.label }}
                  <el-tag v-if="row.simulated" size="small" type="warning" class="temp-badge" effect="plain">临时成员</el-tag>
                </strong>
                <small v-if="row.subline">{{ row.subline }}</small>
              </span>
              <button
                v-if="row.kind === 'dept' && row.hasChildren"
                type="button"
                class="picker-drill"
                @click.stop="drillDown(row)"
              >下级</button>
            </div>
            <p v-if="!loading && !currentRows.length" class="picker-empty">暂无成员或部门</p>
          </div>
        </template>

        <div v-else v-loading="loading" class="picker-list search-mode">
          <div
            v-for="row in searchRows"
            :key="row.key"
            class="picker-row"
            :class="{ checked: isRowChecked(row), disabled: isRowDisabled(row) }"
          >
            <el-checkbox
              v-if="mode === 'multiple' || row.kind === 'user'"
              :model-value="isRowChecked(row)"
              :disabled="isRowDisabled(row)"
              @change="(v: boolean | string | number) => toggleRow(row, v === true || v === 'true')"
            />
            <span class="picker-row-icon" :class="row.kind">
              <el-icon v-if="row.kind === 'dept'"><OfficeBuilding /></el-icon>
              <el-icon v-else><User /></el-icon>
            </span>
            <span class="picker-row-main" @click="onRowMainClick(row)">
              <strong>
                {{ row.label }}
                <el-tag v-if="row.simulated" size="small" type="warning" class="temp-badge" effect="plain">临时成员</el-tag>
              </strong>
              <small v-if="row.subline">{{ row.subline }}</small>
            </span>
            <button
              v-if="row.kind === 'dept' && mode === 'multiple'"
              type="button"
              class="picker-drill"
              @click.stop="toggleRow(row, !isRowChecked(row))"
            >选中</button>
          </div>
          <p v-if="!loading && !searchRows.length" class="picker-empty">没有匹配结果</p>
        </div>
      </section>

      <section class="picker-right">
        <div class="picker-right-head">
          <span>已选：{{ selectedItems.length }} 个</span>
        </div>
        <el-input
          v-model="rightKeyword"
          clearable
          placeholder="搜索已选"
          class="picker-search right-search"
        >
          <template #prefix>
            <el-icon><Search /></el-icon>
          </template>
        </el-input>
        <div class="picker-list selected-list">
          <div
            v-for="item in filteredSelectedItems"
            :key="item.key"
            class="picker-selected-row"
          >
            <span class="picker-row-icon" :class="item.kind">
              <el-icon v-if="item.kind === 'dept'"><OfficeBuilding /></el-icon>
              <el-icon v-else><User /></el-icon>
            </span>
            <span class="picker-row-main">
              <strong>
                {{ item.label }}
                <el-tag v-if="item.simulated" size="small" type="warning" class="temp-badge" effect="plain">临时成员</el-tag>
              </strong>
              <small v-if="item.subline">{{ item.subline }}</small>
            </span>
            <button type="button" class="picker-remove" aria-label="移除" @click="removeSelected(item.key)">×</button>
          </div>
          <p v-if="!filteredSelectedItems.length" class="picker-empty">
            {{ selectedItems.length ? '没有匹配的已选项' : (mode === 'multiple' ? '暂未选择成员' : '暂未选择') }}
          </p>
        </div>
      </section>
    </div>

    <template #footer>
      <el-button
        v-if="mode === 'single' && clearable && selectedItems.length > 0"
        data-testid="clear-member-selection"
        @click="onClear"
      >清空选择</el-button>
      <el-button @click="close">取消</el-button>
      <el-button type="primary" :loading="confirming" @click="confirm">确定</el-button>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { ElMessage } from 'element-plus';
import { OfficeBuilding, Search, User } from '@element-plus/icons-vue';
import {
  getVirtualDeptTree,
  getVirtualDeptMembers,
  listTempMembers,
} from '@/api/orgAdminApi';
import {
  listMemberPickerDeptChildren,
  listMemberPickerDeptMembers,
  listProjectMemberCandidates,
  type ProjectMemberOption,
} from '@/api/projectApi';

export type MemberPickerMode = 'single' | 'multiple';

export interface MemberTableRow extends ProjectMemberOption {
  directDepartmentName?: string | null;
  simulated?: boolean;
}

type OrgKind = 'formal' | 'virtual' | 'temp';

type PickerRow = {
  key: string;
  kind: 'dept' | 'user';
  id: number;
  label: string;
  subline?: string;
  hasChildren?: boolean;
  simulated?: boolean;
  user?: any;
  rawDept?: any;
};

type SelectedItem = {
  key: string;
  kind: 'dept' | 'user';
  id: number;
  label: string;
  subline?: string;
  orgKind: OrgKind;
  simulated?: boolean;
  user?: any;
};

type Crumb = { key: string; label: string; deptId: number | null };

const props = withDefaults(
  defineProps<{
    visible: boolean;
    mode: MemberPickerMode;
    members?: ProjectMemberOption[] | null;
    modelValue?: number | number[] | null;
    title?: string;
    remoteSearch?: boolean;
    clearable?: boolean;
    /** 禁止选择临时/模拟成员（项目负责人等场景） */
    forbidSimulated?: boolean;
  }>(),
  {
    members: null,
    modelValue: null,
    title: '选择成员',
    remoteSearch: true,
    clearable: false,
    forbidSimulated: false,
  },
);

const emit = defineEmits<{
  (e: 'update:visible', v: boolean): void;
  (e: 'update:modelValue', v: number | number[] | null): void;
  (e: 'confirm', payload: { userIds: number[]; rows: MemberTableRow[] }): void;
}>();

const activeTab = ref<OrgKind>('formal');
const keyword = ref('');
const rightKeyword = ref('');
const loading = ref(false);
const confirming = ref(false);
/** 正式组织改为懒加载（/projects/member-candidates），不再依赖管理员 formal-tree */
const virtualTree = ref<any[]>([]);
const breadcrumbs = ref<Crumb[]>([{ key: 'root', label: '组织架构', deptId: null }]);
const currentRows = ref<PickerRow[]>([]);
const searchRows = ref<PickerRow[]>([]);
const selectedMap = ref<Map<string, SelectedItem>>(new Map());
const rowCache = ref(new Map<number, MemberTableRow>());
let searchTimer: ReturnType<typeof setTimeout> | null = null;
let virtualTreeLoaded = false;

const isSearching = computed(() => !!keyword.value.trim());
const selectedItems = computed(() => Array.from(selectedMap.value.values()));
const filteredSelectedItems = computed(() => {
  const q = rightKeyword.value.trim().toLowerCase();
  if (!q) return selectedItems.value;
  return selectedItems.value.filter((item) => {
    const blob = [item.label, item.subline].filter(Boolean).join(' ').toLowerCase();
    return blob.includes(q);
  });
});

watch(
  () => props.visible,
  (v) => {
    if (!v) {
      resetState();
      return;
    }
    // 打开时同步已选（不依赖 dialog @opened，便于测试与快速回显）
    seedFromModelValue();
  },
);

function resetState() {
  keyword.value = '';
  rightKeyword.value = '';
  loading.value = false;
  confirming.value = false;
  currentRows.value = [];
  searchRows.value = [];
  selectedMap.value = new Map();
  activeTab.value = 'formal';
  breadcrumbs.value = [{ key: 'root', label: '组织架构', deptId: null }];
  virtualTree.value = [];
  virtualTreeLoaded = false;
}

function close() {
  emit('update:visible', false);
}

function isSimulatedUser(u: any): boolean {
  if (!u) return false;
  if (u.simulated === true || u.virtual === true) return true;
  const pid = String(u.platformUserId || '');
  return pid.startsWith('virtual_') || pid.startsWith('account_');
}

function isActiveUser(u: any): boolean {
  const s = String(u?.status || 'ACTIVE').toUpperCase();
  return s === 'ACTIVE' || s === '启用' || s === 'ENABLED';
}

function normalizeUser(u: any) {
  const id = Number(u?.id ?? u?.userId);
  if (!Number.isFinite(id) || id <= 0) return null;
  return { ...u, id, userId: id };
}

function userSubline(u: any) {
  const bits = [
    u.username ? `账号 ${u.username}` : '',
    u.employeeNo,
    u.departmentName || u.directDepartmentName,
  ].filter(Boolean);
  return bits.join(' · ') || '';
}

function toMemberRow(u: any): MemberTableRow {
  const id = Number(u?.id ?? u?.userId);
  return {
    userId: id,
    realName: u.realName || u.displayName || u.username || `用户 #${id}`,
    departmentName: u.departmentName ?? undefined,
    directDepartmentName: u.directDepartmentName ?? undefined,
    platformUserId: u.platformUserId,
    avatarUrl: u.avatarUrl,
    position: u.position,
    simulated: isSimulatedUser(u),
  };
}

function mergeRowCache(rows: MemberTableRow[]) {
  const map = new Map(rowCache.value);
  for (const r of rows) {
    const id = Number(r.userId);
    if (!Number.isFinite(id) || id <= 0) continue;
    map.set(id, { ...(map.get(id) || {}), ...r, userId: id });
  }
  rowCache.value = map;
}

function seedFromModelValue() {
  const map = new Map<string, SelectedItem>();
  const ids: number[] = [];
  if (props.mode === 'multiple') {
    const raw = props.modelValue;
    if (Array.isArray(raw)) {
      for (const n of raw) {
        const id = Number(n);
        if (Number.isFinite(id) && id > 0) ids.push(id);
      }
    }
  } else if (props.modelValue != null && props.modelValue !== '') {
    const id = Number(props.modelValue);
    if (Number.isFinite(id) && id > 0) ids.push(id);
  }

  const memberById = new Map<number, ProjectMemberOption>();
  for (const m of props.members || []) {
    const id = Number(m.userId);
    if (Number.isFinite(id)) memberById.set(id, m);
  }

  for (const id of ids) {
    const m = memberById.get(id);
    const row = m
      ? toMemberRow(m)
      : ({ userId: id, realName: `用户 #${id}` } as MemberTableRow);
    mergeRowCache([row]);
    map.set(`user:${id}`, {
      key: `user:${id}`,
      kind: 'user',
      id,
      label: row.realName || `用户 #${id}`,
      subline: row.departmentName || '',
      orgKind: row.simulated ? 'temp' : 'formal',
      simulated: !!row.simulated,
      user: row,
    });
  }
  selectedMap.value = map;
}

function findDept(list: any[], id: number): any | null {
  for (const d of list || []) {
    if (Number(d.id) === id) return d;
    const hit = findDept(d.children || [], id);
    if (hit) return hit;
  }
  return null;
}

function collectDescendantDeptIds(node: any): number[] {
  const id = Number(node?.id);
  const ids = Number.isFinite(id) && id > 0 ? [id] : [];
  for (const child of node?.children || []) {
    ids.push(...collectDescendantDeptIds(child));
  }
  return ids;
}

async function loadDeptUsers(orgKind: OrgKind, deptId: number, includeSubtree = false): Promise<any[]> {
  if (orgKind === 'formal') {
    const list = await listMemberPickerDeptMembers(deptId, includeSubtree);
    return (list || []).map(normalizeUser).filter(Boolean) as any[];
  }
  const list = await getVirtualDeptMembers(deptId);
  return (list || []).map(normalizeUser).filter(Boolean) as any[];
}

async function ensureVirtualTree() {
  if (virtualTreeLoaded) return;
  try {
    virtualTree.value = (await getVirtualDeptTree()) || [];
  } catch {
    virtualTree.value = [];
  }
  virtualTreeLoaded = true;
}

function toUserRow(u: any): PickerRow | null {
  const user = normalizeUser(u);
  if (!user || !isActiveUser(user)) return null;
  const simulated = isSimulatedUser(user);
  if (props.forbidSimulated && simulated) {
    return {
      key: `user:${user.id}`,
      kind: 'user',
      id: user.id,
      label: user.realName || user.displayName || user.username || `#${user.id}`,
      subline: (userSubline(user) ? `${userSubline(user)} · ` : '') + '不可选为项目负责人',
      simulated: true,
      user,
    };
  }
  return {
    key: `user:${user.id}`,
    kind: 'user',
    id: user.id,
    label: user.realName || user.displayName || user.username || `#${user.id}`,
    subline: userSubline(user),
    simulated,
    user,
  };
}

async function loadCurrentLevel() {
  loading.value = true;
  try {
    if (activeTab.value === 'temp') {
      const users = ((await listTempMembers()) || []).map(normalizeUser).filter(Boolean) as any[];
      currentRows.value = users
        .map(toUserRow)
        .filter(Boolean) as PickerRow[];
      return;
    }

    if (activeTab.value === 'formal') {
      const current = breadcrumbs.value[breadcrumbs.value.length - 1];
      const parentDeptId = current.deptId;
      const depts = (await listMemberPickerDeptChildren(parentDeptId)) || [];
      const deptRows: PickerRow[] = depts.map((d) => ({
        key: `dept:formal:${d.deptId}`,
        kind: 'dept' as const,
        id: Number(d.deptId),
        label: d.name || `部门#${d.deptId}`,
        // 叶子部门也可能有成员，始终允许下钻查看人员
        hasChildren: true,
        subline: d.hasChildren ? '含下级部门' : undefined,
      }));
      let userRows: PickerRow[] = [];
      if (parentDeptId != null && parentDeptId > 0) {
        const users = await loadDeptUsers('formal', parentDeptId, false);
        userRows = users.map(toUserRow).filter(Boolean) as PickerRow[];
      }
      currentRows.value = [...deptRows, ...userRows];
      return;
    }

    // virtual
    await ensureVirtualTree();
    const tree = virtualTree.value;
    const current = breadcrumbs.value[breadcrumbs.value.length - 1];
    let depts: any[] = [];
    if (current.deptId == null) {
      depts = tree;
    } else {
      const node = findDept(tree, current.deptId);
      depts = node?.children || [];
    }

    const deptRows: PickerRow[] = depts.map((d: any) => {
      const childDepts = Array.isArray(d.children) ? d.children : [];
      return {
        key: `dept:virtual:${d.id}`,
        kind: 'dept' as const,
        id: Number(d.id),
        label: d.name || `部门#${d.id}`,
        hasChildren: true,
        rawDept: d,
        subline: childDepts.length ? `${childDepts.length} 个子部门` : undefined,
      };
    });

    let userRows: PickerRow[] = [];
    if (current.deptId != null) {
      const users = await loadDeptUsers('virtual', current.deptId);
      userRows = users.map(toUserRow).filter(Boolean) as PickerRow[];
    }

    currentRows.value = [...deptRows, ...userRows];
  } catch {
    currentRows.value = [];
  } finally {
    loading.value = false;
  }
}

async function onOpened() {
  seedFromModelValue();
  rightKeyword.value = '';
  keyword.value = '';
  activeTab.value = 'formal';
  breadcrumbs.value = [{ key: 'root', label: '组织架构', deptId: null }];
  await loadCurrentLevel();
}

function switchTab(tab: OrgKind) {
  if (activeTab.value === tab) return;
  activeTab.value = tab;
  breadcrumbs.value = [
    {
      key: 'root',
      label: tab === 'formal' ? '组织架构' : tab === 'virtual' ? '虚拟组织' : '临时成员',
      deptId: null,
    },
  ];
  void loadCurrentLevel();
}

function drillDown(row: PickerRow) {
  if (row.kind !== 'dept') return;
  breadcrumbs.value = [
    ...breadcrumbs.value,
    { key: `d:${row.id}`, label: row.label, deptId: row.id },
  ];
  void loadCurrentLevel();
}

function goBreadcrumb(idx: number) {
  if (idx < 0 || idx >= breadcrumbs.value.length - 1) return;
  breadcrumbs.value = breadcrumbs.value.slice(0, idx + 1);
  void loadCurrentLevel();
}

function onSearchInput() {
  if (!props.remoteSearch) return;
  if (searchTimer) clearTimeout(searchTimer);
  searchTimer = setTimeout(runSearch, 260);
}

async function runSearch() {
  const q = keyword.value.trim();
  if (!q) {
    searchRows.value = [];
    return;
  }
  loading.value = true;
  try {
    if (activeTab.value === 'temp') {
      const temps = ((await listTempMembers(q)) || []).map(toUserRow).filter(Boolean) as PickerRow[];
      searchRows.value = temps;
      return;
    }

    if (activeTab.value === 'formal') {
      // 使用项目成员候选接口（PM/SE 可读），支持拼音检索
      const users = await listProjectMemberCandidates(q);
      searchRows.value = (users || []).map(toUserRow).filter(Boolean) as PickerRow[];
      return;
    }

    // virtual：按部门名本地过滤 + 已加载树下成员名匹配不够时仅显示部门命中
    await ensureVirtualTree();
    const tree = virtualTree.value;
    const deptHits: PickerRow[] = [];
    const walk = (list: any[], path: string[]) => {
      for (const d of list || []) {
        const name = String(d.name || '');
        const nextPath = [...path, name];
        if (name.toLowerCase().includes(q.toLowerCase())) {
          deptHits.push({
            key: `dept:virtual:${d.id}`,
            kind: 'dept',
            id: Number(d.id),
            label: name,
            subline: nextPath.join(' / '),
            hasChildren: true,
            rawDept: d,
          });
        }
        walk(d.children || [], nextPath);
      }
    };
    walk(tree, []);

    // 候选搜索走正式通讯录关键字（虚拟成员多为正式账号挂到虚拟组）
    const users = await listProjectMemberCandidates(q);
    const userRows: PickerRow[] = (users || []).map(toUserRow).filter(Boolean) as PickerRow[];
    searchRows.value = [...deptHits, ...userRows];
  } catch {
    searchRows.value = [];
  } finally {
    loading.value = false;
  }
}

function isRowDisabled(row: PickerRow) {
  return row.kind === 'user' && !!props.forbidSimulated && !!row.simulated;
}

function isRowChecked(row: PickerRow) {
  if (props.mode === 'single' && row.kind === 'user') {
    return selectedMap.value.has(`user:${row.id}`);
  }
  return selectedMap.value.has(row.key);
}

function onRowMainClick(row: PickerRow) {
  if (row.kind === 'dept') {
    if (row.hasChildren) drillDown(row);
    else if (props.mode === 'multiple') toggleRow(row, !isRowChecked(row));
    return;
  }
  toggleRow(row, !isRowChecked(row));
}

function toggleRow(row: PickerRow, checked: boolean) {
  if (isRowDisabled(row)) return;
  if (props.mode === 'single') {
    if (row.kind !== 'user') return;
    const next = new Map<string, SelectedItem>();
    if (checked) {
      next.set(`user:${row.id}`, {
        key: `user:${row.id}`,
        kind: 'user',
        id: row.id,
        label: row.label,
        subline: row.subline,
        orgKind: activeTab.value,
        simulated: row.simulated,
        user: row.user,
      });
      if (row.user) mergeRowCache([toMemberRow(row.user)]);
    }
    selectedMap.value = next;
    return;
  }
  const next = new Map(selectedMap.value);
  if (checked) {
    next.set(row.key, {
      key: row.key,
      kind: row.kind,
      id: row.id,
      label: row.label,
      subline: row.subline,
      orgKind: activeTab.value,
      simulated: row.simulated,
      user: row.user,
    });
    if (row.kind === 'user' && row.user) mergeRowCache([toMemberRow(row.user)]);
  } else {
    next.delete(row.key);
  }
  selectedMap.value = next;
}

function removeSelected(key: string) {
  const next = new Map(selectedMap.value);
  next.delete(key);
  selectedMap.value = next;
}

async function resolveSelectedUserIds(): Promise<{ ids: number[]; simulatedIds: number[] }> {
  const ids = new Set<number>();
  const simulatedIds = new Set<number>();
  for (const item of selectedItems.value) {
    if (item.kind === 'user') {
      if (props.forbidSimulated && item.simulated) continue;
      ids.add(item.id);
      if (item.simulated) simulatedIds.add(item.id);
      continue;
    }
    if (props.mode !== 'multiple') continue;
    if (item.orgKind === 'formal') {
      const users = await loadDeptUsers('formal', item.id, true);
      for (const u of users) {
        if (!u || !isActiveUser(u)) continue;
        const simulated = isSimulatedUser(u);
        if (props.forbidSimulated && simulated) continue;
        ids.add(u.id);
        mergeRowCache([toMemberRow(u)]);
        if (simulated) simulatedIds.add(u.id);
      }
      continue;
    }
    const node = findDept(virtualTree.value, item.id);
    const deptIds = node ? collectDescendantDeptIds(node) : [item.id];
    for (const deptId of deptIds) {
      const users = await loadDeptUsers('virtual', deptId);
      for (const u of users) {
        if (!u || !isActiveUser(u)) continue;
        const simulated = isSimulatedUser(u);
        if (props.forbidSimulated && simulated) continue;
        ids.add(u.id);
        mergeRowCache([toMemberRow(u)]);
        if (simulated) simulatedIds.add(u.id);
      }
    }
  }
  return { ids: Array.from(ids), simulatedIds: Array.from(simulatedIds) };
}

function onClear() {
  emit('update:modelValue', null);
  emit('confirm', { userIds: [], rows: [] });
  emit('update:visible', false);
}

async function confirm() {
  if (confirming.value) return;
  confirming.value = true;
  try {
    const { ids, simulatedIds } = await resolveSelectedUserIds();
    if (props.forbidSimulated && props.mode === 'single' && selectedItems.value.some((i) => i.simulated)) {
      ElMessage.warning('临时成员不能担任项目负责人');
      return;
    }
    if (simulatedIds.length > 0) {
      ElMessage.info('已选含临时成员：仅站内通知，无飞书/企微');
    }
    if (props.mode === 'multiple') {
      emit('update:modelValue', ids);
      emit('confirm', {
        userIds: ids,
        rows: ids.map((id) => rowCache.value.get(id)).filter(Boolean) as MemberTableRow[],
      });
    } else {
      const id = ids[0] ?? null;
      emit('update:modelValue', id);
      const row = id != null ? rowCache.value.get(id) : undefined;
      emit('confirm', {
        userIds: id != null ? [id] : [],
        rows: row ? [row] : [],
      });
    }
    emit('update:visible', false);
  } finally {
    confirming.value = false;
  }
}
</script>

<style scoped>
.picker-body {
  display: grid;
  grid-template-columns: minmax(0, 1.15fr) minmax(0, 0.85fr);
  height: 480px;
  border: 1px solid var(--org-border, #e8edf3);
  border-radius: 8px;
  overflow: hidden;
}
.picker-left,
.picker-right {
  min-width: 0;
  min-height: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #fff;
  overflow: hidden;
}
.picker-left {
  border-right: 1px solid var(--org-border, #e8edf3);
  padding: 12px;
}
.picker-right {
  padding: 12px;
}
.picker-search {
  flex-shrink: 0;
  margin-bottom: 10px;
}
.picker-search.right-search {
  margin-bottom: 8px;
}
.picker-nav {
  flex-shrink: 0;
}
.picker-tabs {
  display: flex;
  gap: 16px;
  margin-bottom: 8px;
  border-bottom: 1px solid var(--org-border-light, #eef2f6);
}
.picker-tabs button {
  border: 0;
  background: transparent;
  padding: 4px 2px 8px;
  font-size: 13px;
  color: var(--org-text-secondary, #5b6b7c);
  cursor: pointer;
  position: relative;
}
.picker-tabs button.active {
  color: var(--org-primary, #1264e2);
  font-weight: 600;
}
.picker-tabs button.active::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 2px;
  border-radius: 1px;
  background: var(--org-primary, #1264e2);
}
.picker-breadcrumb {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
  margin-bottom: 8px;
  font-size: 12px;
}
.picker-breadcrumb .crumb {
  border: 0;
  background: transparent;
  padding: 0;
  color: var(--org-primary, #1264e2);
  cursor: pointer;
  font-size: 12px;
}
.picker-breadcrumb .crumb:not(.current)::after {
  content: '>';
  margin-left: 4px;
  color: var(--org-text-muted, #8993a2);
}
.picker-breadcrumb .crumb.current {
  color: var(--org-text-secondary, #5b6b7c);
  cursor: default;
}
.picker-hint {
  margin: 0 0 8px;
  font-size: 12px;
  color: var(--org-text-muted, #8993a2);
  line-height: 1.4;
}
.picker-right-head {
  flex-shrink: 0;
  margin-bottom: 8px;
  font-size: 13px;
  color: var(--org-text, #202a37);
  font-weight: 600;
}
.picker-list {
  flex: 1 1 0;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}
.picker-row,
.picker-selected-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 6px;
  border-radius: 6px;
  min-height: 40px;
}
.picker-row:hover,
.picker-selected-row:hover {
  background: #f5f7fa;
}
.picker-row.checked {
  background: #f0f4f8;
}
.picker-row.disabled {
  opacity: 0.45;
}
.picker-row-icon {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #fff;
  font-size: 14px;
}
.picker-row-icon.dept {
  background: #3370ff;
}
.picker-row-icon.user {
  background: #8f959e;
}
.picker-row-main {
  flex: 1;
  min-width: 0;
  cursor: pointer;
}
.picker-row-main strong {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  font-size: 13px;
  font-weight: 500;
  color: var(--org-text, #202a37);
}
.picker-row-main small {
  display: block;
  font-size: 12px;
  color: var(--org-text-muted, #8993a2);
}
.temp-badge {
  vertical-align: middle;
}
.picker-drill {
  border: 0;
  background: transparent;
  color: var(--org-primary, #1264e2);
  font-size: 12px;
  cursor: pointer;
  flex-shrink: 0;
  padding: 4px 2px;
}
.picker-drill::after {
  content: ' >';
}
.picker-remove {
  border: 0;
  background: transparent;
  color: var(--org-text-muted, #8993a2);
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  flex-shrink: 0;
}
.picker-empty {
  margin: 24px 0;
  text-align: center;
  color: var(--org-text-muted, #8993a2);
  font-size: 12px;
}
</style>
